// ═══════════════════════════════════════════════════════════════════════════
// copRules.js — THE Cost-of-Production rules. One home, two callers.
//
// These lived inside `GrainMarketingView` in src/App.jsx, which meant the only
// thing that could ever evaluate a COP line was that one screen. The SILAS
// shadow estimator (Farm Economics Increment 2B) has to produce numbers that
// reconcile with the farmer-facing tool to the cent, and the way that goes wrong
// is not a wrong formula — it is a SECOND formula. Same reason the grain target
// ladder moved to src/grainTargets.js and the corn yield model moved to
// src/miYieldModel.js: two engines that disagreed became one engine with two
// skins. Keep it that way.
//
// PURE ON PURPOSE. No React, no fetch, no component state, no imports. Every
// function is a deterministic map from its arguments to a number, so it can run
// in the browser, in a Node checker (tools/check-cop-rules.js) and in a
// serverless writer without any of them carrying a copy.
//
// ── THE ONE IMPURITY, AND HOW IT WAS REMOVED ───────────────────────────────
// The whole chain used to be impure through exactly one leaf: the crop-insurance
// default reads the FARMER'S ACTUAL producer-paid premium, which lives in
// component state. That is separated here into:
//     data acquisition   — the caller resolves the farmer's $/ac (App.jsx does it
//                          from `selectedFarmer`; 2B will do it from Supabase)
//     pure calculation   — everything below, which takes that number as an input
// `insurancePremiumPerAcreFrom()` is the acquisition-agnostic half of that split:
// give it the premium/acre rows and it returns the same figure the UI shows.
//
// ── ARITY IS THE CONTRACT ──────────────────────────────────────────────────
// `insurancePerAcre` is the LAST parameter of most functions here, and dropping
// it does NOT throw — it silently falls back to the static extension-budget
// default, which is a wrong number on a farmer's screen rather than an error.
// That is precisely the failure mode `tools/check-shared-rules.js` exists for
// (matchSupplementals without baseRows, plantedSplit without unitsByKey), so
// these functions are REGISTERED THERE. Change the arity deliberately, never at
// a call site.
//
// ── SIGNS: A CREDIT IS METADATA, NOT A NEGATIVE NUMBER ─────────────────────
// `grain_cop.line_items` and `cop_estimate_lines.cost_per_acre` both store
// MAGNITUDES. Whether a line subtracts is decided by `credit: true` on the
// category, applied in exactly one place — `copSigned`. Wheat's straw credit is
// the live counterexample to summing the raw values, so nothing may compute a
// total as a naive sum of stored line values; it goes through `copSum`.
// ═══════════════════════════════════════════════════════════════════════════

// Cost-of-Production category schemas. Detailed = extension-grade line items
// grouped into budget sections (mirrors the FAST / OSU / UW-Extension enterprise
// budget skeleton: inputs → crop protection → machine operations → drying/storage
// → overhead → land); streamlined = a 7-row quick version. Both persist to
// grain_cop.line_items; the active mode's keys sum to the cost/acre that feeds the
// Crop Plan, and within detailed mode each `group` gets its own subtotal.
//
// Two machinery accounting methods are supported, and the UI footnote warns not to
// mix them: (a) CUSTOM-RATE — leave "Machinery — deprec. & interest" blank and enter
// each Field Operations pass as an all-in $/ac (the custom rate bundles fuel, repairs,
// labor AND ownership for that pass); or (b) OWNED — fill the machinery ownership line
// with annual depreciation + interest per acre, and enter Field Operations passes as
// operating cost only (fuel + repairs + labor). `hint` values are rough NY/PA
// custom-rate ranges (2022 NASS custom-rate survey) shown as faint guidance.
export const COP_CATEGORIES = {
  detailed: [
    // Seed & Fertility
    { key: 'seed', label: 'Seed', group: 'Seed & Fertility' },
    { key: 'n', label: 'Nitrogen (N)', group: 'Seed & Fertility' },
    { key: 'p', label: 'Phosphate (P₂O₅)', group: 'Seed & Fertility' },
    { key: 'k', label: 'Potash (K₂O)', group: 'Seed & Fertility' },
    { key: 'lime', label: 'Lime', group: 'Seed & Fertility' },
    { key: 'micros', label: 'Micronutrients / Sulfur', group: 'Seed & Fertility' },
    // Crop Protection (chemistry)
    { key: 'herbicide', label: 'Herbicide', group: 'Crop Protection' },
    { key: 'fungicide', label: 'Fungicide', group: 'Crop Protection' },
    { key: 'insecticide', label: 'Insecticide', group: 'Crop Protection' },
    { key: 'adjuvants', label: 'Adjuvants / Other', group: 'Crop Protection' },
    // Field Operations — all-in $/ac per pass (custom-rate style), multiplied by
    // a pass count. `perPass: true` splits the line into rate × passes in the UI;
    // the stored number stays the RATE, and the pass count lives under
    // `<key>__passes` (absent = 1), so every row saved before pass counts existed
    // still totals exactly what it did.
    { key: 'till', label: 'Tillage', group: 'Field Operations', hint: '~$18–24/pass', perPass: true },
    { key: 'plant', label: 'Planting', group: 'Field Operations', hint: '~$38', perPass: true },
    { key: 'fertapp', label: 'Fertilizer / lime application', group: 'Field Operations', hint: '~$8–12', perPass: true },
    { key: 'spray', label: 'Spraying', group: 'Field Operations', hint: '~$9–16', perPass: true },
    { key: 'harvest', label: 'Harvest / combine', group: 'Field Operations', hint: '~$40–60', perPass: true },
    { key: 'graincart', label: 'Grain cart', group: 'Field Operations', hint: '~$6–10', perPass: true },
    { key: 'hauling', label: 'Hauling / trucking', group: 'Field Operations', hint: '~$0.20/bu', perPass: true },
    // Drying & Storage
    { key: 'drying', label: 'Drying', group: 'Drying & Storage' },
    { key: 'storage', label: 'Storage & handling', group: 'Drying & Storage' },
    // Overhead & Financing
    { key: 'machinery', label: 'Machinery — deprec. & interest', group: 'Overhead & Financing', hint: 'owned equip' },
    { key: 'labor', label: 'Hired labor (non-field)', group: 'Overhead & Financing' },
    { key: 'insurance', label: 'Crop insurance', group: 'Overhead & Financing' },
    { key: 'interest', label: 'Operating interest', group: 'Overhead & Financing' },
    { key: 'overhead', label: 'Misc / overhead', group: 'Overhead & Financing' },
    // Land
    { key: 'land', label: 'Land (cash rent / ownership)', group: 'Land' },
    // Byproduct credit - straw baled & sold. `credit: true` subtracts it from the
    // cost total (lowers net cost/acre & break-even). `crops` limits it to wheat.
    { key: 'straw', label: 'Straw credit (baled & sold)', group: 'Byproduct Credit', credit: true, crops: ['Wheat'], hint: 'lowers net cost' },
  ],
  streamlined: [
    { key: 's_seed', label: 'Seed' },
    { key: 's_fert', label: 'Fertilizer & Lime' },
    { key: 's_chem', label: 'Chemicals (herb/fung/insect)' },
    { key: 's_insurance', label: 'Crop Insurance' },
    { key: 's_power', label: 'Machinery, Fuel & Repairs' },
    { key: 's_labor', label: 'Labor & Overhead' },
    { key: 's_land', label: 'Land (cash rent / ownership)' },
    { key: 's_straw', label: 'Straw credit (baled & sold)', credit: true, crops: ['Wheat'] },
  ],
};

// Starting-point Cost of Production per crop ($/acre), drawn from Northeast/WNY
// extension enterprise-budget ranges (Cornell / Penn State / Ohio State). These are
// PRE-FILL DEFAULTS only - every line is overridable, and any value the user types
// wins. Field Operations use all-in custom rates (they already include fuel, repairs,
// labor and depreciation), so Machinery - deprec. & interest defaults to 0 to avoid
// double-counting. Streamlined keys mirror the same totals grouped.
export const COP_DEFAULTS = {
  detailed: {
    Corn:     { seed:120, n:120, p:45, k:45, lime:15, micros:10, herbicide:45, fungicide:20, insecticide:8, adjuvants:5, till:22, plant:38, fertapp:10, spray:18, harvest:45, graincart:8, hauling:20, drying:35, storage:15, machinery:0, labor:0, insurance:25, interest:20, overhead:25, land:175 },
    Soybeans: { seed:75, n:0, p:40, k:50, lime:15, micros:8, herbicide:55, fungicide:18, insecticide:5, adjuvants:5, till:22, plant:35, fertapp:8, spray:20, harvest:40, graincart:8, hauling:12, drying:5, storage:12, machinery:0, labor:0, insurance:20, interest:18, overhead:22, land:175 },
    Wheat:    { seed:55, n:90, p:40, k:30, lime:15, micros:8, herbicide:30, fungicide:25, insecticide:5, adjuvants:4, till:20, plant:30, fertapp:12, spray:15, harvest:40, graincart:6, hauling:12, drying:10, storage:10, machinery:0, labor:0, insurance:18, interest:15, overhead:20, land:175, straw:75 },
  },
  streamlined: {
    Corn:     { s_seed:120, s_fert:235, s_chem:78, s_insurance:25, s_power:210, s_labor:45, s_land:175 },
    Soybeans: { s_seed:75, s_fert:113, s_chem:83, s_insurance:20, s_power:162, s_labor:40, s_land:175 },
    Wheat:    { s_seed:55, s_fert:183, s_chem:64, s_insurance:18, s_power:155, s_labor:35, s_land:175, s_straw:75 },
  },
};

// The Field Operations lines — the ones priced per pass. Used to decide whether a
// COP row is booking machinery the custom-rate way (ownership already inside each
// pass) or the owned way (ownership on its own line). See `copMachMethod`.
export const COP_FIELD_OP_KEYS = COP_CATEGORIES.detailed.filter(c => c.perPass).map(c => c.key);

// The two COP line keys that carry the crop-insurance cost, one per mode. Named
// rather than string-tested at each site so the shadow estimator and the UI agree
// on which line the real premium belongs to.
export const COP_INSURANCE_KEYS = ['insurance', 's_insurance'];

// ── Crop naming ─────────────────────────────────────────────────────────────
// The grain tool speaks Title Case ('Corn' / 'Soybeans' / 'Wheat'); the insurance
// tables speak UPPERCASE and split wheat into WHEAT and WINTER WHEAT. This is the
// ONE place that reconciliation lives, so a premium can never be matched to a crop
// by one rule here and a different rule in the estimator.
export function copCropMatches(insuranceCrop, grainCrop) {
  const target = String(grainCrop).toUpperCase();
  const u = String(insuranceCrop || '').toUpperCase();
  return target === 'WHEAT' ? (u === 'WHEAT' || u === 'WINTER WHEAT') : u === target;
}

// ── Insurance: the PURE half of the real-premium lookup ─────────────────────
// The farmer's ACTUAL crop-insurance cost per acre = net (producer-paid, after
// subsidy) premium across base coverage + SCO/ECO endorsements, over insured
// acres. Null when we have no premium/acres for the crop.
//
// Acres come from the base coverage rows; SCO/ECO contribute PREMIUM ONLY and
// never acres, because an endorsement sits on top of acres the base line already
// counted — adding them again would halve the $/ac.
//
// `resolveFallbackAcres` is a THUNK, not a number, and is called only when the
// base rows carry no acres. That keeps the original laziness exactly: the caller's
// fallback lookup (App.jsx's `cropInsurance(crop)`) is not run unless it is needed.
export function insurancePremiumPerAcreFrom({ crop, approvedYields, scoEcoEndorsements, resolveFallbackAcres }) {
  let netPrem = 0, acres = 0;
  (approvedYields || []).filter(a => copCropMatches(a.crop, crop))
    .forEach(a => { netPrem += Number(a.netPremium) || 0; acres += Number(a.acres) || 0; });
  (scoEcoEndorsements || []).filter(e => copCropMatches(e.crop, crop))
    .forEach(e => { netPrem += Number(e.netPremium) || 0; });
  if (acres <= 0 && typeof resolveFallbackAcres === 'function') acres = Number(resolveFallbackAcres()) || 0;
  if (acres <= 0 || netPrem <= 0) return null;
  return Math.round(netPrem / acres);
}

// ── The line rules ──────────────────────────────────────────────────────────

// Starting value for a COP line when the user hasn't entered one. The crop-
// insurance line uses the farmer's real premium/acre when the caller resolved one;
// everything else uses the static per-crop extension-budget default.
//
// `insurancePerAcre` may be a NUMBER or a THUNK returning one, and the thunk is
// invoked ONLY on an insurance key — which is the whole reason it is allowed.
// Resolving the farmer's premium means running the liability-first guarantee
// resolver over every coverage row; the original code paid that cost once, for the
// one line that needs it, and a plain number parameter would have moved it to
// every line of every render. Same numbers, same work.
export function copDefault(mode, crop, key, insurancePerAcre) {
  if (COP_INSURANCE_KEYS.includes(key)) {
    const prem = typeof insurancePerAcre === 'function' ? insurancePerAcre() : insurancePerAcre;
    if (prem != null) return Number(prem);
  }
  const d = COP_DEFAULTS[mode]?.[crop]?.[key];
  return d != null ? Number(d) : null;
}

// Per-line COP value: the user's entry if present (even 0), else the crop's
// pre-fill extension-budget default. Clearing a field reverts it to the default.
export function copVal(mode, crop, items, key, insurancePerAcre) {
  if (items[key] != null) return Number(items[key]) || 0;
  const d = copDefault(mode, crop, key, insurancePerAcre);
  return d != null ? d : 0;
}

// Categories that apply to a crop (e.g. Straw credit is wheat-only).
export function copCats(mode, crop) {
  return COP_CATEGORIES[mode].filter(c => !c.crops || c.crops.includes(crop));
}

// Pass count for a Field Operations line. Stored alongside the rate under
// `<key>__passes`. Absent means 1 — which is what makes every COP row written
// before pass counts existed total exactly what it always did.
export function copPasses(items, key) {
  const raw = items[`${key}__passes`];
  if (raw == null || raw === '') return 1;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 1;
}

// Total of the per-pass Field Operations lines (detailed mode only).
export function copFieldOpsTotal(mode, crop, items, insurancePerAcre) {
  if (mode !== 'detailed') return 0;
  return COP_FIELD_OP_KEYS.reduce(
    (s, k) => s + copVal(mode, crop, items, k, insurancePerAcre) * copPasses(items, k), 0);
}

// ── Machinery accounting method — the double-count guard ────────────────────
// There are two legitimate ways to book machinery, and CHARGING BOTH inflates
// the cost by the whole ownership line:
//   'custom' — Field Operations are all-in custom rates (each pass already
//              carries fuel + repairs + labor + depreciation), so the standalone
//              "Machinery — deprec. & interest" line must NOT also be charged.
//   'owned'  — Field Operations are operating cost only (fuel + repairs + labor)
//              and ownership is carried on the machinery line.
// The footnote warned about this and the math didn't enforce it, so a row could
// quietly charge ownership twice: Kennedy's CY2026 wheat carried $100 machinery
// on top of $153 of custom-rate passes and read $623/ac where the real number is
// $523 — an $8.77 break-even against a true $7.37. That row is still in the book
// and is a fixture in tools/check-cop-rules.js.
//
// Once the user picks, the choice is stored under `__machMethod`. With nothing
// stored we INFER from the numbers rather than defaulting blind: a row carrying
// BOTH a machinery figure and priced field-op passes is the double-count case,
// and the all-in passes are the more complete number, so ownership drops out. A
// row with machinery but no priced passes IS the owned method and is left alone.
// Inferring rather than picking a fixed default is what stops this from silently
// rewriting a farm that was already booked correctly.
//
// NOTE FOR THE SHADOW ESTIMATOR (Increment 2B): an engine that prices operations
// as custom-rate equivalents is emitting the 'custom' method and must therefore
// write `__machMethod: 'custom'` (or no machinery line at all). Emitting both a
// machinery figure and priced passes without saying which method it means lands
// on the inference above — which happens to be right today, but "happens to be
// right" is how the $623 got shipped the first time. Say it explicitly.
export function copMachMethod(mode, crop, items, insurancePerAcre) {
  const stored = items.__machMethod;
  if (stored === 'custom' || stored === 'owned') return stored;
  const mach = copVal(mode, crop, items, 'machinery', insurancePerAcre);
  return (mach > 0 && copFieldOpsTotal(mode, crop, items, insurancePerAcre) > 0) ? 'custom' : 'owned';
}

// What a line contributes in $/acre. Per-pass lines multiply out; everything
// else is the entered figure as-is. The machinery ownership line contributes
// NOTHING under the custom-rate method — that is the guard, applied in the one
// place every total (row, group subtotal, footer, Crop Plan cost/acre) reads.
export function copLine(mode, crop, items, cat, insurancePerAcre) {
  if (cat.key === 'machinery' && copMachMethod(mode, crop, items, insurancePerAcre) === 'custom') return 0;
  const rate = copVal(mode, crop, items, cat.key, insurancePerAcre);
  return cat.perPass ? rate * copPasses(items, cat.key) : rate;
}

// Signed contribution of a line: credits (straw) subtract from the cost total.
// THE ONLY PLACE A SIGN IS APPLIED. Stored values are magnitudes everywhere else.
export function copSigned(mode, crop, items, cat, insurancePerAcre) {
  return (cat.credit ? -1 : 1) * copLine(mode, crop, items, cat, insurancePerAcre);
}

// The cost/acre for a crop under one mode. This — never a raw sum of line_items —
// is the total. NOTE it is a NET figure: the by-product credit is already inside.
export function copSum(mode, crop, items, insurancePerAcre) {
  return copCats(mode, crop).reduce(
    (s, c) => s + copSigned(mode, crop, items, c, insurancePerAcre), 0);
}

// ── GROSS, CREDIT, NET — three names for what used to be one opaque number ───
// `copSum` returns NET cost/acre. Anything downstream that wants to subtract a
// by-product credit has ALREADY had it subtracted, and subtracting again is the
// bug that read Kennedy's wheat break-even as $5.96/bu against a true $7.37.
//
// So the credit is exposed as its own value rather than left buried, and the
// relationship is stated once, here:
//
//     gross production cost  =  net  +  credit
//     net grain cost         =  copSum(...)          <- what getCost() returns
//     credit                 =  copCreditTotal(...)
//
// Kevin, 2026-08-15: "preserve gross production cost and byproduct/straw credit as
// conceptually separate values wherever possible so SILAS can explain gross COP ->
// byproduct credit -> net grain COP rather than hiding the credit inside an opaque
// total." These two functions are that separation. Wheat straw is the only credit
// category today; corn and soybeans return 0 and are unaffected by any of it.
export function copCreditTotal(mode, crop, items, insurancePerAcre) {
  return copCats(mode, crop).filter(c => c.credit)
    .reduce((s, c) => s + copLine(mode, crop, items, c, insurancePerAcre), 0);
}

// Gross production cost/acre — before the by-product credit is applied.
export function copGrossTotal(mode, crop, items, insurancePerAcre) {
  return copSum(mode, crop, items, insurancePerAcre)
       + copCreditTotal(mode, crop, items, insurancePerAcre);
}
